import React, { useEffect, useState, useCallback } from "react";
import {
  object,
  boolean,
  string,
  array,
  ValidationError as YupValidationError
} from "yup";
import { useIntl } from "react-intl";
import MultiInput from "~/components/MultiInput";
import InputErrorMessage from "~/components/InputErrorMessage";
import { validateCpf, validateCnpj } from "~/utils/validate";
import { processOperation } from "~/api";
import { formatCpfCnpj } from "~/utils/format";
import getYupValidationErrors from "~/utils/getYupValidationErrors";
import "./styles.scss";
import { useOperation } from "~/hooks/useOperation";

export const saveInformations = async ({ data, startFrom, formatMessage }) => {
  if (!data)
    return {
      error: formatMessage({ id: "erro-api" })
    };

  let documents;
  if (data.cpf_cnpj && Array.isArray(data.cpf_cnpj)) {
    documents = data.cpf_cnpj.map(doc => doc.label);
  }

  let specificUses = [];
  if (data.uso_especifico && Array.isArray(data.uso_especifico)) {
    specificUses = data.uso_especifico.map(uso => uso.label);
  }

  const schema = object({
    nome: string().required(formatMessage({ id: "validacao-nome" })),
    descricao: string().nullable(),
    is_pronaf: boolean(),
    identificador: string().nullable(),
    ref_bacen: string().nullable(),
    proposta_inicio_tipo: string().oneOf(["car", "gleba"]).required(),
    cpf_cnpj: array()
      .of(string())
      .min(1, formatMessage({ id: "validacao-documentos" })),
    uso_especifico: array()
      .of(string())
  });

  try {
    const tmp = {
      ...data,
      cpf_cnpj: documents,
      uso_especifico: specificUses,
      proposta_inicio_tipo: startFrom
    };

    schema.validateSync(tmp, {
      abortEarly: false
    });

    const response = await processOperation(tmp);
    return response;
  } catch (e) {
    if (e instanceof YupValidationError) {
      const errors = getYupValidationErrors(e);

      return {
        error: `${formatMessage({ id: "ocorreram-erros" })}: ${Object.values(
          errors
        ).join("; ")}`
      };
    }

    return {
      error: e.response.data.message || formatMessage({ id: "erro-api" })
    };
  }
};

const OperationInformations = ({ data, setError, stepLabel, onChange }) => {
  const { operation } = useOperation();
  const { formatMessage } = useIntl();
  const [encodedStartData, setEncodedStartData] = useState(null);
  const [invalidDocuments, setInvalidDocuments] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const formatDocument = useCallback(
    unformattedValue => {
      const value = unformattedValue.replace(/[. /-]/g, "");

      if (!validateCpf(value) && !validateCnpj(value)) {
        setInvalidDocuments([...invalidDocuments, unformattedValue]);
        return null;
      }

      return {
        value,
        label: formatCpfCnpj(value)
      };
    },
    [invalidDocuments, setInvalidDocuments]
  );

  const sortDataByKeys = _data => {
    const dataSorted = Object.keys(_data)
      .sort()
      .reduce((acc, key) => {
        return {
          ...acc,
          [key]: _data[key]
        };
      }, {});

    return dataSorted;
  };

  useEffect(() => {
    let saver = setInterval(() => {
      if (loadingExisting || !operation.id) return;
      const sorted = JSON.stringify(sortDataByKeys(data));
      if (encodedStartData === sorted) return;

      setEncodedStartData(sorted);

      const saveData = {
        ...JSON.parse(sorted),
        id: operation.id
      };

      saveInformations({
        data: saveData,
        setError,
        formatMessage
      });
    }, 500);

    return () => {
      clearInterval(saver);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingExisting, encodedStartData]);

  useEffect(() => {
    if (!operation.id) {
      return;
    }

    const newData = onChange([
      {
        field: "nome",
        value: operation.nome
      },
      {
        field: "identificador",
        value: operation.identificador
      },
      {
        field: "ref_bacen",
        value: operation.ref_bacen
      },
      {
        field: "is_pronaf",
        value: operation.is_pronaf
      },
      {
        field: "descricao",
        value: operation.descricao
      },
      {
        field: "cpf_cnpj",
        value: (operation.requisicao?.cpf_cnpj ?? []).map(pessoa => {
          return formatDocument(pessoa);
        })
      },
      {
        field: "uso_especifico",
        value: (operation.requisicao?.uso_especifico ?? []).map(uso => {
          return uso;
        })
      }
    ]);

    const dataSorted = sortDataByKeys(newData);
    setEncodedStartData(JSON.stringify(dataSorted));
    setLoadingExisting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation.id, formatDocument]);

  return (
    <>
      <h3 className="">{stepLabel}</h3>

      <div className="tile is-ancestor">
        <div className="tile is-6 is-vertical">
          <p>
            <>{formatMessage({ id: "mensagem-input-operacao" })}</>
          </p>
        </div>
      </div>

      <div className="tile is-ancestor">
        <div className="tile is-6 is-vertical">
          <div className="field">
            <label className="label">
              {formatMessage({ id: "nome-operacao" })}
            </label>
            <div className="control">
              <input
                className="input"
                type="text"
                id="report-title"
                value={data.nome ?? ""}
                onChange={e =>
                  onChange([
                    {
                      field: "nome",
                      value: e.target.value
                    }
                  ])
                }
              />
            </div>
          </div>
        </div>
        <div className="tile is-6 is-vertical input-info input-has-label">
          {formatMessage({ id: "ajuda-nome-operacao" })}
        </div>
      </div>

      <div className="tile is-ancestor">
        <div className="tile is-6 is-vertical">
          <div className="field">
            <label className="label">
              {formatMessage({ id: "referencia-bacen" })}
            </label>
            <div className="control">
              <input
                className="input"
                type="text"
                id="report-title"
                value={data.ref_bacen ?? ""}
                onChange={e => {
                  if (isNaN(e.target.value)) {
                    e.target.value = data.ref_bacen;
                    return;
                  }

                  const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                  onChange([
                    {
                      field: "ref_bacen",
                      value: onlyNums
                    }
                  ]);
                }}
              />
            </div>
          </div>
        </div>
        <div className="tile is-6 is-vertical input-info input-has-label">
          {formatMessage({ id: "ajuda-referencia-bacen" })}
        </div>
      </div>

      <div className="tile is-ancestor">
        <div className="tile is-6 is-vertical">
          <div className="field">
            <label className="label">{formatMessage({ id: "contrato" })}</label>
            <div className="control">
              <input
                className="input"
                type="text"
                id="report-title"
                value={data.identificador ?? ""}
                onChange={e => {
                  if (isNaN(e.target.value)) {
                    e.target.value = data.identificador;
                    return;
                  }

                  onChange([
                    {
                      field: "identificador",
                      value: e.target.value
                    }
                  ]);
                }}
              />
            </div>
          </div>
        </div>
        <div className="tile is-6 is-vertical input-info input-has-label">
          {formatMessage({ id: "ajuda-contrato" })}
        </div>
      </div>

      <div className="tile is-ancestor">
        <div className="tile is-6 is-vertical">
          <div
            className={`field${
              data.uso_especifico && data.uso_especifico.length ? " filled" : ""
            }`}
          >
            <label className="label">
              {formatMessage({ id: "atividades-economicas" })}
            </label>
            <div className="control">
              <MultiInput
                id="report-document"
                value={data.uso_especifico ?? []}
                onChange={multi => {
                  if (!data.uso_especifico) data.uso_especifico = [];
                  if (multi.length === data.uso_especifico.length) return;

                  onChange([
                    {
                      field: "uso_especifico",
                      value: multi
                    }
                  ]);
                }}
              />
            </div>
          </div>
        </div>
        <div className="tile is-6 is-vertical input-info input-has-label">
          {formatMessage({ id: "ajuda-analise-atividades-economicas" })}
        </div>
      </div>

      <div className="tile is-ancestor">
        <div className="tile is-6 is-vertical">
          <div
            className={`field${
              data.cpf_cnpj && data.cpf_cnpj.length ? " filled" : ""
            }`}
          >
            <label className="label">
              {formatMessage({ id: "Documento-Proponentes-Participantes" })}
            </label>
            <div className="control">
              <MultiInput
                id="report-document"
                optionFormatter={formatDocument}
                value={data.cpf_cnpj ?? []}
                onChange={multi => {
                  if (!data.cpf_cnpj) data.cpf_cnpj = [];
                  if (multi.length === data.cpf_cnpj.length) return;

                  onChange([
                    {
                      field: "cpf_cnpj",
                      value: multi
                    }
                  ]);
                }}
              />
            </div>
            {invalidDocuments.length > 0 && (
              <InputErrorMessage
                message={`${formatMessage(
                  { id: "erro-documento-invalido" },
                  { num: invalidDocuments.length }
                )}: ${invalidDocuments.map(doc => `"${doc}"`).join(", ")}`}
                onClear={() => setInvalidDocuments([])}
              />
            )}
          </div>
        </div>
        <div className="tile is-6 is-vertical input-info input-has-label">
          {formatMessage({ id: "ajuda-analise-documento" })}
        </div>
      </div>

      <div className="tile is-ancestor">
        <div className="tile is-6 is-vertical">
          <div className="field">
            <label className="label">
              {formatMessage({ id: "descricao" })}
            </label>
            <div className="control">
              <textarea
                rows="3"
                className="textarea"
                type="text"
                id="report-description"
                value={data.descricao ?? ""}
                onChange={e =>
                  onChange([
                    {
                      field: "descricao",
                      value: e.target.value
                    }
                  ])
                }
              />
            </div>
          </div>
        </div>
        <div className="tile is-6 is-vertical input-info input-has-label">
          {formatMessage({ id: "ajuda-descricao" })}
        </div>
      </div>

      <div className="tile is-ancestor">
        <div className="tile is-6 is-vertical">
          <div className="field">
            <div className="control">
              <input
                type="checkbox"
                id="pronaf-type"
                name="pronaf-type"
                value={data.is_pronaf ?? false}
                onChange={e =>
                  onChange([
                    {
                      field: "is_pronaf",
                      value: e.target.checked
                    }
                  ])
                }
              />{" "}
              {formatMessage({ id: "pronaf" })}
            </div>
          </div>
        </div>
        <div className="tile is-6 is-vertical input-info">
          {formatMessage({ id: "ajuda-pronaf" })}
        </div>
      </div>
    </>
  );
};

export default OperationInformations;
